import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { postApi, adApi } from '../utils/api';
import { useCategoryStore } from '../stores/categoryStore';
import { Post, PaginatedResponse, Ad } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { MessageCircle, Eye, Heart } from 'lucide-react';
import { t } from '../stores/languageStore';
import AdDisplay from '../components/AdDisplay';

const CategoryView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { getCategoryById } = useCategoryStore();

  const [posts, setPosts] = React.useState<Post[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [pagination, setPagination] = React.useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });
  const [categoryAds, setCategoryAds] = React.useState<Ad[]>([]);
  const [adsLoading, setAdsLoading] = React.useState(true);

  const category = getCategoryById(parseInt(id || '0'));

  React.useEffect(() => {
    if (id) {
      fetchPosts();
    }
    fetchAds();
  }, [id, page]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response: PaginatedResponse<Post> = await postApi.getPosts({
        page,
        pageSize: 20,
        categoryId: parseInt(id || '0'),
      });
      if (response.success) {
        setPosts(response.data || []);
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAds = async () => {
    try {
      setAdsLoading(true);
      const response = await adApi.getActiveAds();
      if (response.success && response.data && response.data.category_page) {
        setCategoryAds(response.data.category_page);
      }
    } catch (error) {
      console.error('Failed to fetch ads:', error);
    } finally {
      setAdsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: zhCN });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 版块信息 */}
      <div className="card p-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {category?.name || t('category.title')}
        </h1>
        {category?.description && (
          <p className="text-gray-600 mt-2">{category.description}</p>
        )}
        <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
          <span>{t('category.postsCount')}: {pagination.total}</span>
        </div>
      </div>

      {/* 分类页面广告位 */}
      {!adsLoading && categoryAds.length > 0 && (
        <div className="card p-4">
          <AdDisplay ads={categoryAds} />
        </div>
      )}

      {/* 帖子列表 */}
      {posts.length > 0 ? (
        <>
          <div className="space-y-4">
            {posts.map((post) => (
              <article key={post.id} className="card p-4 hover:shadow-md transition-shadow">
                <div className="flex gap-4">
                  <Link to={`/user/${post.author_id}`} className="flex-shrink-0">
                    <img
                      src={post.author_avatar || `https://ui-avatars.com/api/?name=${post.author_name}&background=3b82f6&color=fff`}
                      alt={post.author_name}
                      className="w-12 h-12 rounded-full"
                    />
                  </Link>

                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/post/${post.id}`}
                      className="text-lg font-semibold text-gray-900 hover:text-primary-600 line-clamp-1"
                    >
                      {post.title}
                    </Link>

                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <Link
                        to={`/user/${post.author_id}`}
                        className="hover:text-primary-600"
                      >
                        {post.author_name}
                      </Link>
                      {post.author_level_icon && <span className="ml-1">{post.author_level_icon}</span>}
                      {post.author_titles && <span className="ml-1 text-xs text-purple-600">{post.author_titles}</span>}
                      <span className="text-xs text-gray-400 ml-1">Lv.{post.author_level}</span>
                      <span>{formatDate(post.created_at)}</span>
                    </div>

                    <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Eye className="w-4 h-4 mr-1" />
                        {post.views}
                      </span>
                      <span className="flex items-center">
                        <MessageCircle className="w-4 h-4 mr-1" />
                        {post.replies_count}
                      </span>
                      <span className="flex items-center">
                        <Heart className="w-4 h-4 mr-1" />
                        {post.likes}
                      </span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* 分页 */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn btn-secondary disabled:opacity-50"
              >
                {t('home.previousPage')}
              </button>
              <span className="text-sm text-gray-600">
                {t('home.pageInfo', { page, totalPages: pagination.totalPages })}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={page === pagination.totalPages}
                className="btn btn-secondary disabled:opacity-50"
              >
                {t('home.nextPage')}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-500">{t('category.noPosts')}</p>
        </div>
      )}
    </div>
  );
};

export default CategoryView;
